import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

type EditableReview = {
  id: string;
  asin: string;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  reviewDate?: string | null;
  verifiedPurchase: boolean;
};

interface AddReviewFormProps {
  auditId: string;
  asins: string[];
  onChange?: () => void;
  review?: EditableReview;
  onCancel?: () => void;
}

export function AddReviewForm({
  auditId,
  asins,
  onChange,
  review,
  onCancel,
}: AddReviewFormProps) {
  const isEdit = Boolean(review);
  const [asin, setAsin] = useState(review?.asin ?? asins[0] ?? "");
  const [rating, setRating] = useState(String(review?.rating ?? 5));
  const [title, setTitle] = useState(review?.title ?? "");
  const [body, setBody] = useState(review?.body ?? "");
  const [reviewerName, setReviewerName] = useState(review?.reviewerName ?? "");
  const [reviewDate, setReviewDate] = useState(
    review?.reviewDate ? review.reviewDate.slice(0, 10) : ""
  );
  const [verifiedPurchase, setVerifiedPurchase] = useState(
    review?.verifiedPurchase ?? false
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const payload = {
      asin,
      rating: parseInt(rating, 10),
      title: title || null,
      body,
      reviewerName: reviewerName || null,
      reviewDate: reviewDate ? new Date(reviewDate).toISOString() : null,
      verifiedPurchase,
    };

    const res = isEdit
      ? await api(`/admin/reviews/${review!.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        })
      : await api(`/admin/audits/${auditId}/reviews`, {
          method: "POST",
          body: JSON.stringify(payload),
        });

    if (!res.ok) {
      setError(res.error ?? "Something went wrong. Please try again.");
      setSubmitting(false);
      return;
    }

    if (!isEdit) {
      setRating("5");
      setTitle("");
      setBody("");
      setReviewerName("");
      setReviewDate("");
      setVerifiedPurchase(false);
    }
    setSubmitting(false);
    onChange?.();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="asin">ASIN</Label>
          <Select
            value={asin}
            onValueChange={(v) => setAsin(v ?? "")}
            disabled={submitting}
          >
            <SelectTrigger id="asin" className="font-mono">
              <SelectValue placeholder="Select an ASIN" />
            </SelectTrigger>
            <SelectContent>
              {asins.map((a) => (
                <SelectItem key={a} value={a} className="font-mono">
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="rating">Rating</Label>
          <Select
            value={rating}
            onValueChange={(v) => setRating(v ?? "5")}
            disabled={submitting}
          >
            <SelectTrigger id="rating">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 4, 3, 2, 1].map((r) => (
                <SelectItem key={r} value={r.toString()}>
                  {r} star{r !== 1 ? "s" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">
          Title{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={submitting}
          placeholder="Review headline…"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="body">
          Review body <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="body"
          required
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={submitting}
          placeholder="Paste the full review text here…"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="reviewerName">
            Reviewer name{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="reviewerName"
            type="text"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            disabled={submitting}
            placeholder="John Doe"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="reviewDate">
            Review date{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="reviewDate"
            type="date"
            value={reviewDate}
            onChange={(e) => setReviewDate(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="verifiedPurchase"
          checked={verifiedPurchase}
          onCheckedChange={(v) => setVerifiedPurchase(v === true)}
          disabled={submitting}
        />
        <Label htmlFor="verifiedPurchase" className="font-normal">
          Verified Purchase
        </Label>
      </div>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting && <Loader2 className="animate-spin" />}
          {submitting
            ? isEdit
              ? "Saving…"
              : "Adding review…"
            : isEdit
              ? "Save changes"
              : "Add review"}
        </Button>
        {isEdit && onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
